import React, { useState } from "react";
import { motion } from "framer-motion";
import { FiLock, FiSun, FiMoon } from "react-icons/fi";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAuthStore } from "../stores/authStore";

export default function HomePage() {
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  const login = useAuthStore(s => s.login);


const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post("https://vision.viewmax.net/api/users/login/", {
        email,
        password,
      });
      
      // ✅ NEW: Store user in Zustand
      login(res.data);
      
      // ✅ Keep these for API calls (we still need tokens)
      localStorage.setItem("access", res.data.access);
      localStorage.setItem("refreshToken", res.data.refresh);
      
      // ✅ Keep these for now (we'll remove later when MainDashboard uses store)
      localStorage.setItem("userId", res.data.user.id);
      localStorage.setItem("userGlobalRole", res.data.user.global_role);
      localStorage.setItem("userEmail", res.data.user.email);
      localStorage.setItem("userName", res.data.user.name);
      
      navigate("/dashboard");
    } catch (err) {
      console.error("Login failed:", err);
      const msg = err.response?.data?.detail || "Login failed";
      alert(msg);
    } finally {
      setLoading(false);
    }
  };


  const v1_handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post("https://vision.viewmax.net/api/users/login/", {
        email,
        password,
      });
      localStorage.setItem("access", res.data.access);
      localStorage.setItem("refreshToken", res.data.refresh);
      localStorage.setItem("userId", res.data.user.id);
      localStorage.setItem("userGlobalRole", res.data.user.global_role);
      localStorage.setItem("userEmail", res.data.user.email);
      localStorage.setItem("userName", res.data.user.name);
      navigate("/dashboard");
    } catch (err) {
      console.error("Login failed:", err);
      const msg = err.response?.data?.detail || "Login failed";
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  // 🎨 Theme-based styles
  const bgLeft =
    theme === "dark" ? "bg-neutral-800 text-gray-100" : "bg-neutral-50 text-gray-900";
  const inputBg =
    theme === "dark" ? "bg-neutral-800 border-gray-700" : "bg-neutral-100 border-gray-300";
  const mutedText = theme === "dark" ? "text-gray-400" : "text-gray-600";

  return (
    <div className={`min-h-screen flex transition-colors duration-500 ${bgLeft}`}>
      {/* LEFT SIDE: Login */}
      <div className="w-full lg:w-1/2 flex flex-col relative">
        {/* 🔘 Subtle theme toggle */}
        <button
          onClick={toggleTheme}
          className="absolute top-5 right-6 p-2 rounded-full border border-gray-600 hover:bg-gray-700/30 transition"
          title="Toggle theme"
        >
          {theme === "dark" ? (
            <FiSun className="text-yellow-400" size={18} />
          ) : (
            <FiMoon className="text-gray-700" size={18} />
          )}
        </button>

        {/* Logo */}
        <header className="flex justify-center px-8 py-10">
          <motion.img
            src="/images/viewmax_logo.png"
            alt="Viewmax Logo"
            className="h-14 object-contain brightness-110 contrast-125 ]"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          />
        </header>

        {/* Minimal login form (no box) */}
        <div className="flex flex-1 items-center justify-center px-10">
          <motion.form
            onSubmit={handleLogin}
            className="w-full max-w-sm space-y-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl font-bold mb-2">Welcome Back</h1>
            <p className={`${mutedText} mb-6`}>Sign in to your Vision dashboard</p>

            <div>
              <label className="block text-sm mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full rounded-lg px-4 py-3 border outline-none focus:ring-2 focus:ring-blue-600 ${inputBg}`}
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="block text-sm mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full rounded-lg px-4 py-3 border outline-none focus:ring-2 focus:ring-blue-600 ${inputBg}`}
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 transition text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <FiLock />
              {loading ? "Signing In..." : "Log In"}
            </button>
          </motion.form>
        </div>

        <footer className={`text-xs ${mutedText} text-center py-6`}>
          © {new Date().getFullYear()} Viewmax Inc. All rights reserved.
        </footer>
      </div>

      {/* RIGHT SIDE: Hero image */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden text-white">
        <motion.div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('/images/viewmax_bg.jpg')",
          }}
          animate={{ scale: [1, 1.1], x: ["0%", "-5%"], y: ["0%", "-5%"] }}
          transition={{
            duration: 25,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut",
          }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 flex flex-col justify-center items-start px-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-4xl font-extrabold mb-4 leading-snug drop-shadow-lg"
          >
            Vision That Works <br /> When You Can’t Watch
          </motion.h2>
          <motion.p
            className="text-lg text-gray-200 max-w-md drop-shadow-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            AI-powered monitoring built for enterprises — secure, reliable, and always
            watching.
          </motion.p>
        </div>
      </div>
    </div>
  );
}

